/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  NgbActiveModal,
  NgbModal,
  NgbTooltipConfig,
} from '@ng-bootstrap/ng-bootstrap';
import {
  WorkflowStatusService,
  SqdStatusState,
  SqdStatus,
  TrayStatus,
} from '../../services/workflow-status.service';
import { RunningWorkflow } from '../../services/workflow.service';
import { Subscription } from 'rxjs';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { XdaStringsService } from '../../services/xda-strings.service';
import { EditorModalComponent } from './editor-modal.component';
import { BrowserService } from '../../services/browser.service';

@Component({
  selector: 'jobs-modal-component',
  templateUrl: './jobs-modal.component.html',
  styleUrls: ['./jobs-modal.component.less'],
  animations: [
    trigger('fadeOut', [
      state(
        'void',
        style({
          opacity: 0,
        }),
      ),
      transition('* => void', animate(500)),
    ]),
  ],
  standalone: false,
})
export class JobsModalComponent implements OnInit, OnDestroy {
  workflows: RunningWorkflow[];
  statusStates: typeof SqdStatusState = SqdStatusState;
  FORCE_ENABLE_CANCEL_TIME_IN_MINISECONDS = 14400000; // 4 hours: 4 * 60 * 60 * 1000
  expandedWorkflow: RunningWorkflow | null = null;
  statusSub: Subscription;
  serviceStatusNames = {
    SDE_TRANSLATE: 'SDE_TRANSLATING_DOCUMENT',
    SDE_CONVERT_AUDIO: 'SDE_CONVERTING_AUDIO',
    SDE_HANDWRITING_TEXT: 'SDE_CONVERTING_HANDWRITING',
    SDE_CONVERT_MS_OFFICE: 'SDE_CONVERTING_MS_OFFICE',
    SDE_DOCUMENT_MERGE: 'SDE_MERGING_DOCUMENTS',
    SDE_EXTRACTIVE_SUMMARY: 'SDE_SUMMARIZING_CONTENT',
    SDE_REDACT: 'SDE_REDACTING_DOCUMENT',
    SDE_DOCUMENT_PROTECTION: 'SDE_PROTECTING_DOCUMENT',
    SDE_CONVERT_TO_PDF1: 'SDE_CONVERTING_PDF',
    SDE_SEND_FAX: 'SDE_SENDING_FAX',
  };

  constructor(
    public activeModal: NgbActiveModal,
    private tooltipConfig: NgbTooltipConfig,
    public workflowStatusService: WorkflowStatusService,
    public xdaStringsService: XdaStringsService,
    public modalService: NgbModal,
    private browserService: BrowserService,
  ) {
    this.tooltipConfig.tooltipClass = 'xrx-tooltip';
  }

  ngOnInit(): void {
    this.workflows = this.workflowStatusService.processingWorkflows;
    this.translateInvalidPageRange();
    this.pageCountCheckStatus(true);
    this.statusSub = this.workflowStatusService.trayStatusChange.subscribe(
      (data) => this.statusChange(data),
    );
  }

  expand(workflow: RunningWorkflow): void {
    if (this.expandedWorkflow == workflow) {
      this.expandedWorkflow = null;
    } else {
      this.expandedWorkflow = workflow;
    }
  }

  remove(event: MouseEvent, workflow: RunningWorkflow): void {
    // stop even propagation so expand does not fire
    event.stopPropagation();

    if (workflow.id) {
      this.workflowStatusService.removeWorkflow(workflow.id);
      if (this.workflows.length === 0) {
        this.activeModal.dismiss();
      }
    }
  }

  cancel(event: MouseEvent, workflow: RunningWorkflow): void {
    // stop even propagation so expand does not fire
    event.stopPropagation();
    if (!workflow.waitToBeCanceled) {
      this.workflowStatusService.cancelWorkflow(workflow);
    }
  }

  report(workflow: RunningWorkflow): void {
    if (
      this.isErrorStateArray(workflow.status || []) &&
      !this.isInsufficientCreditStateArray(workflow.status || [])
    ) {
      this.workflowStatusService.errorReportWorkflow(workflow);
    }
  }

  isTerminalStateArray(status: SqdStatus[]): boolean {
    try {
      return (
        status.some((s) => s.state == this.statusStates.Errored) ||
        status.some((s) => s.state == this.statusStates.Canceled) ||
        this.isCompletedStateArray(status)
      );
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  isErrorStateArray(status: SqdStatus[]): boolean {
    try {
      return status.some((s) => s.state == this.statusStates.Errored);
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  getErrorMessage(status: SqdStatus[]): string {
    const errorStatus = status.find(
      (s) => s.state == this.statusStates.Errored,
    );
    const isOutputStatus =
      errorStatus?.serviceId == '00000000-0000-0000-0000-000000000000';
    const errorMessage = isOutputStatus
      ? errorStatus?.statusRaw
      : errorStatus?.status;
    return errorMessage ?? '';
  }

  isCompletedStateArray(status: SqdStatus[]): boolean {
    try {
      return status.every((s) => s.state == this.statusStates.Completed);
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  isAPendingStateArray(status: SqdStatus[]): boolean {
    return (
      this.isPendingHeldStateArray(status) ||
      this.isPendingDownloadStateArray(status) ||
      this.isPendingPrintStateArray(status)
    );
  }

  isPendingHeldStateWorkflow(workflow: RunningWorkflow): boolean {
    try {
      const result = this.isPendingHeldStateArray(workflow.status || []);
      if (!result && workflow.interimDownloaded == true) {
        workflow.interimDownloaded = false;
      }

      if (!result && workflow.interimContinued == true) {
        workflow.interimContinued = false;
      }
      return result;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  isPendingHeldStateArray(status: SqdStatus[]): boolean {
    try {
      return status.some((s) => s.state == this.statusStates.PendingHeld);
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  isPendingDownloadStateArray(status: SqdStatus[]): boolean {
    try {
      return status.some((s) => s.state == this.statusStates.PendingDownload);
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  isPendingPrintStateArray(status: SqdStatus[]): boolean {
    try {
      return status.some((s) => s.state == this.statusStates.PendingPrint);
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  isCanceledStateArray(status: SqdStatus[]): boolean {
    try {
      return status.some((s) => s.state == this.statusStates.Canceled);
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  isErrorState(status: SqdStatus): boolean {
    return status.state == this.statusStates.Errored;
  }

  isCompletedState(status: SqdStatus): boolean {
    return status.state == this.statusStates.Completed;
  }

  isPendingDownloadState(status: SqdStatus): boolean {
    return (
      status.state == this.statusStates.PendingDownload ||
      status.state == this.statusStates.PendingHeld
    );
  }

  isPendingPrintState(status: SqdStatus): boolean {
    return status.state == this.statusStates.PendingPrint;
  }

  isOutputTypePrint(outputOptions: any): boolean {
    return outputOptions.some((o: any) => o.value == 'Print');
  }

  download(event: MouseEvent, workflow: RunningWorkflow) {
    // stop even propagation so expand does not fire
    event.stopPropagation();
    this.workflowStatusService.downloadAndFinish(workflow);
  }

  downloadInterim(event: MouseEvent, workflow: RunningWorkflow) {
    // stop even propagation so expand does not fire
    event.stopPropagation();
    if (!workflow.interimDownloaded) {
      workflow.interimDownloaded = true;
      this.workflowStatusService.downloadInterim(workflow);
    }
  }

  editInterim(event: MouseEvent, workflow: RunningWorkflow) {
    // stop even propagation so expand does not fire
    event.stopPropagation();
    const modalRef = this.modalService.open(EditorModalComponent, {
      windowClass: 'xrx-modal modal-fullscreen',
    });
    modalRef.componentInstance.workflow = workflow;

    modalRef.closed.subscribe((reason) => {
      if (reason == 'saved') {
        workflow.interimDownloaded = false;
      }
    });
  }

  continue(event: MouseEvent, workflow: RunningWorkflow) {
    // stop even propagation so expand does not fire
    event.stopPropagation();
    workflow.interimContinued = true;
    this.workflowStatusService.continueWorkflow(workflow);
  }

  statusChange(data: TrayStatus) {
    // check for stucked fax process
    if (data.processingStates > 0) {
      this.workflows.forEach((wf) => {
        // use name matching to short the search for now
        // name matching can be removed to apply the logic for any workflows if needed in future.
        if (
          wf.services?.find(
            (sv) =>
              sv.name === 'SDE_SEND_FAX' &&
              sv.status?.find((st) => st.state === SqdStatusState.Processing),
          )
        ) {
          // workflow in processing state, check the time elapsed
          const initTime =
            typeof wf.initiatedAt === 'string'
              ? new Date(wf.initiatedAt)
              : wf.initiatedAt;

          if (initTime) {
            const elapsed = new Date().getTime() - initTime.getTime();
            if (elapsed > this.FORCE_ENABLE_CANCEL_TIME_IN_MINISECONDS) {
              // force enable Cancel
              wf.forceEnableCancel = true;
            }
          }
        }
      });
    }

    this.translateInvalidPageRange();
    this.pageCountCheckStatus(false);
    if (this.workflowStatusService.processingWorkflows.length === 0) {
      this.activeModal.dismiss();
    }
  }

  pageCountCheckStatus(onInit: boolean) {
    this.workflows.forEach((w) => {
      // if onInit check for page count value in input.pageCount
      if (onInit && w.input.pageCount != '0') {
        w.pageCount = w.input.pageCount;
      } else {
        // if input.pageCount isn't set than check the job status to see if it is ready to get a page count
        w.status?.forEach((s) => {
          // check the status for each running job and only check for page count if upload is complete and pageCount has not already been set
          if (
            s.status.indexOf('SDE_UPLOAD_COMPLETE') !== -1 &&
            !w.pageCount &&
            w.id
          ) {
            this.getpageCount(w.id);
          }
        });
      }
    });
  }

  getpageCount(id: string) {
    let job;
    // make sure id is a GUID before requesting a page count for a job
    if (
      /^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/.test(id)
    ) {
      // make the call to get the pageCount
      this.workflowStatusService.getWorkflowPageCount(id).then((count) => {
        // make sure count is greater then 0 since sometimes 0 comes back right after upload
        if (Number(count) > 0) {
          // get the job object for the job we just got the page count for
          job = this.workflows.find((w) => w.id == id);
          // set the page count
          if (job) {
            job.pageCount = count;
          }
        }
      });
    }
  }

  translateInvalidPageRange() {
    this.workflows.forEach((w) => {
      w.status?.forEach((s) => {
        if (
          s.state == SqdStatusState.Errored &&
          s.status.indexOf('SDE_INVALID_PAGE_RANGE') !== -1
        ) {
          const splitStatus = s.status.split(':');
          const translatedStatus = this.xdaStringsService.strings[splitStatus[0]];
          s.status = translatedStatus + ': ' + splitStatus[1];
        }
      });
    });
  }

  isDownloadOption(outputOptions: { key: string; value: string }[]) {
    return outputOptions.some((obj) => obj.value === 'Download');
  }

  isInsufficientCreditStateArray(status: SqdStatus[]): boolean {
    try {
      return status.some((s) => s.status == 'SDE_INSUFFICIENT_CREDITS');
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  containsServiceName(status: SqdStatus[], service: string): boolean {
    try {
      return status.some((s) => s.serviceName == service);
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  ngOnDestroy(): void {
    this.statusSub.unsubscribe();
  }
}
